import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { RadioGroupContext } from './context';

import './style.scss';
import {
  AutoSaveState,
  AutoSaveIndicator,
} from '../AutoSaveIndicator/component';

const RadioGroupContainer = styled.div();

declare interface RadioOptionProps {
  label: string | ReactNode;
  value: string;
  disabled?: boolean;
  children?: (selected: boolean) => ReactNode;
}

export class RadioOption extends React.PureComponent<RadioOptionProps> {
  private inputId: string;

  public constructor(props: RadioOptionProps) {
    super(props);
    this.inputId = uniqueId();
  }

  public render(): ReactNode {
    const { label, value, disabled, children } = this.props;

    return (
      <RadioGroupContext.Consumer>
        {({ name, selectedValue, onChange }) => {
          const checked = value === selectedValue;

          return (
            <div className="ol-radio-option">
              <div>
                <input
                  type="radio"
                  id={this.inputId}
                  {...{ name, value, checked, disabled, onChange }}
                />
              </div>
              <div>
                <label
                  className={classNames({ disabled })}
                  htmlFor={this.inputId}
                >
                  {label}
                </label>
                {children ? children(checked) : null}
              </div>
            </div>
          );
        }}
      </RadioGroupContext.Consumer>
    );
  }
}

export declare interface RadioGroupProps {
  label?: string | ReactNode;
  name: string;
  selectedValue?: string;
  children: ReactNode;
  layout?: 'horizontal' | 'vertical';
  className?: string;
  onChange: (value: string) => void;
  autoSave?: boolean;
  autoSaveState?: AutoSaveState;
  required?: boolean;
}

export class RadioGroup extends React.PureComponent<RadioGroupProps> {
  private onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { onChange } = this.props;
    onChange(event.target.value);
  };

  public render(): ReactNode {
    const {
      label = null,
      layout = 'horizontal',
      children = null,
      name,
      className = null,
      selectedValue,
      autoSave = false,
      autoSaveState,
      required = false,
    } = this.props;

    const radioOptions = (
      <div
        className={classNames('ol-radio-group-options', `display-${layout}`)}
      >
        <RadioGroupContext.Provider
          value={{ name, selectedValue, onChange: this.onChange }}
        >
          {children}
        </RadioGroupContext.Provider>
      </div>
    );

    return label ? (
      <RadioGroupContainer>
        <fieldset className={classNames('ol-radio-group', className)}>
          {label && (
            <legend className="ol-text-field-label">
              {required ? `${label} (required)` : label}
              {/* Radio button is slight different from other inputs, so the auto-save
              indicator will be attached to the legend instead */}
              {autoSave && (
                <>
                  {' '}
                  <AutoSaveIndicator state={autoSaveState} />
                </>
              )}
            </legend>
          )}
          {radioOptions}
        </fieldset>
      </RadioGroupContainer>
    ) : (
      // WCAG: Cannot use fieldset if there is no legend
      <div className={classNames('ol-radio-group', className)}>
        {radioOptions}
      </div>
    );
  }
}
